import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { storageUtil } from '../../utils/storage.util';
import { BrowserService } from '../services/browser.service';
import { storageConstant } from 'src/app/constants/storage-constant';
import { AuthService } from '../services/auth.service';
import {
  forceLogoutStaleSession,
  isStoredSessionMissingToken,
} from 'src/app/helpers/login-session.helper';

@Injectable()
export class HeaderInterceptor implements HttpInterceptor {
  constructor(
    private storageUtil: storageUtil,
    private _browserService: BrowserService,
    private _injector: Injector,
  ) {}

  /**
   * loggedInUser in localStorage is JSON.stringify(AES ciphertext of user JSON).
   */
  private getJwtFromLoggedInUserStorage(): string | null {
    try {
      const token = this.storageUtil.getItem(storageConstant.LOGGED_IN_TOKEN);
      if (!token || typeof token !== 'string') {
        return null;
      }

      return token ?? null;
    } catch {
      return null;
    }
  }

  /**
   * When the app still has a session (JWT or encrypted logged-in user blob) but the
   * API rejects the call, clear storage and send the user to login.
   */
  private sessionPresent(): boolean {
    return !!(
      this.getJwtFromLoggedInUserStorage() ||
      this.storageUtil.getItem(storageConstant.LOGGED_IN_USER)
    );
  }

  intercept(
    httpRequest: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Skip IP service requests to avoid circular dependency and CORS issues (Request Info Changes)
    if (httpRequest.url.includes('api.ipify.org')) {
      return next.handle(httpRequest);
    }

    const isBrowser = this._browserService.checkBrowser();

    const isLoginRequest =
      httpRequest.method === 'POST' &&
      httpRequest.url.includes('/authenticate-v2/login');

    let token: string | null = null;
    if (isBrowser && !isLoginRequest) {
      if (isStoredSessionMissingToken()) {
        forceLogoutStaleSession(this._injector.get(AuthService));
        return throwError(
          () =>
            new HttpErrorResponse({
              error: 'Session expired',
              status: 401,
              statusText: 'Unauthorized',
              url: httpRequest.urlWithParams,
            }),
        );
      }
      token = this.getJwtFromLoggedInUserStorage();
    }

    // Clone request and add Authorization header if token exists
    let authRequest = httpRequest;
    if (token) {
      authRequest = httpRequest.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === 401 &&
          isBrowser &&
          !isLoginRequest &&
          this.sessionPresent()
        ) {
          this._injector.get(AuthService).logout(true);
        }
        return throwError(() => error);
      }),
    );
  }
}
